import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Text } from "src/components/base/text";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import { registerKey } from "src/api/auth";
import { toast } from "react-toastify/unstyled";

/**
 * The properties for {@link AddWebauthKeyDialog}
 */
export type AddWebauthKeyDialogProps = {
    /** Callback function when the dialog is closed */
    onClose: () => void;
    /** The type of api call to use */
    type: "invitation" | "user";
    /** Whether the dialog is open or not */
    isOpen: boolean;
};

/**
 * A dialog to add a new webauth key
 */
export default function AddWebauthKeyDialog(props: AddWebauthKeyDialogProps) {
    const [t] = useTranslation("authentication-dialog");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            label: "",
        },
        onSubmit: async ({ value }) => {
            await registerKey(props.type, value.label);
            props.onClose();
            toast.success(t("toast.success-add-webauth-key"));
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.type]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-webauth-key")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit} className={"max-w-lg"}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"label"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.label")}</Label>
                                        <Text className={"my-1"}>{t("description.label-security-key")}</Text>
                                        <Input
                                            autoComplete={"webauthn"}
                                            autoFocus={true}
                                            required={true}
                                            type={"text"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.form.state.errors.length !== 0}
                                        />
                                        {fieldApi.form.state.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.close")}
                                </Button>
                                <PrimaryButton type={"submit"}>{tg("button.register-security-key")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
